import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Ansel, the Ash Scholar (docs/ANTIHEROES.md): the old garrison's scholar, who burned the library so the nightmares
 * would not have it. The titan dreams him in a long scholar's robe turned to black glass, cracked through with fire;
 * a high collar, a skullcap, two embers for eyes and a beard of ash; in his left hand an open book whose pages burn
 * without burning away, in his right a charred staff crowned with a cage of burning pages; a mantle of smoke and ash.
 *
 * Bones as Ser Corvin's (so he walks, reels and falls on the sentinel's clips): body, chest, head, flapF, flapB,
 * armL/foreL/shield (here, the book hand), armR/foreR/sword (here, the staff hand), legs. Faces +Z, `R` on +X.
 */

/** Model height (the staff's crown). */
export const ANSEL_HEIGHT = 2.35;

export const ansel = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 907);
  const { g, rng } = c;
  const k4 = kit(rng);
  const bookFlames: THREE.Mesh[] = [];
  const staffFlames: THREE.Mesh[] = [];

  const glass = metal('#2e2a28', { roughness: 0.18, metalness: 0.45, bumpScale: 0.01 });
  const glassD = metal('#1a1512', { roughness: 0.3, metalness: 0.45, bumpScale: 0.02 });
  const robe = cloth('#1c1612', { side: THREE.DoubleSide });
  const char = std('#140d08', { roughness: 0.92, bumpScale: 0.08 });
  const page = std('#3a2f28', { roughness: 0.9, side: THREE.DoubleSide });
  const fire = emis('#ff9a5a', '#ff6a14', 2.6);
  const fireF = emis('#ffa060', '#ff7a1f', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#1c1612', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: glass greaves under the robe, embers at the knees ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.13, 0.98, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.13, 1.0, 0), V(k * 0.14, 0.56, 0.02), 0.11, 0.085, robe, 7);
    const shinB = bone(hip, V(k * 0.14, 0.55, 0.02), `shin${s}`);
    const lo = shinB.abs;
    M(new THREE.SphereGeometry(0.02, 5, 4), fire, lo).position.set(k * 0.14, 0.55, 0.12);
    limb(lo, V(k * 0.14, 0.53, 0.02), V(k * 0.14, 0.12, 0.0), 0.085, 0.065, glass, 7);
    k4.crack(lo, [V(k * 0.14, 0.45, 0.08), V(k * 0.16, 0.32, 0.075), V(k * 0.13, 0.2, 0.07)], 0.006, fire, 0.025);
    const boot = M(new THREE.BoxGeometry(0.14, 0.09, 0.24), glassD, lo);
    boot.position.set(k * 0.14, 0.045, 0.04);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: the robe's skirt to the shins, singed hems, a cord with a scroll case ---------- */
  const bodyB = bone(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.22, 0.36, 0.62, 10, 1, true), robe, body).position.set(0, 0.74, 0);
  // The singed hem glowing at the bottom edge.
  const hem = M(new THREE.TorusGeometry(0.355, 0.012, 3, 20), fire, body);
  hem.rotation.x = Math.PI / 2;
  hem.position.set(0, 0.44, 0);
  M(new THREE.CylinderGeometry(0.21, 0.23, 0.18, 9), glassD, body).position.set(0, 1.02, 0);
  const cord = M(new THREE.TorusGeometry(0.22, 0.016, 3, 12), char, body);
  cord.rotation.x = Math.PI / 2;
  cord.position.set(0, 1.08, 0);
  const tube = M(new THREE.CylinderGeometry(0.035, 0.035, 0.24, 6), glassD, body);
  tube.position.set(0.2, 0.93, 0.12);
  tube.rotation.z = 0.25;
  const flapF = bone(body, V(0, 1.06, 0.18), 'flapF');
  k4.panel(flapF.abs, V(0, 1.07, 0.21), 0.26, 0.66, 0.02, 1, robe, 0.45);
  const flapB = bone(body, V(0, 1.06, -0.18), 'flapB');
  k4.panel(flapB.abs, V(0, 1.07, -0.21), 0.32, 0.74, 0.02, -1, robe, 0.5);

  /* ---------- chest: a glass robe front cracked with fire, a high stiff collar, ash on the shoulders ---------- */
  const chestB = bone(body, V(0, 1.12, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.38, 0.0, 0.25, 0.28, 0.19, 9, 6);
  k4.swell(chest, glassD, 0, 1.2, 0.01, 0.22, 0.12, 0.17, 8, 4);
  k4.crack(chest, [V(-0.12, 1.6, 0.13), V(-0.04, 1.5, 0.18), V(0.03, 1.4, 0.19), V(-0.02, 1.3, 0.185), V(0.06, 1.18, 0.16)], 0.012, fire, 0.05);
  k4.crack(chest, [V(0.03, 1.4, 0.19), V(0.12, 1.34, 0.17), V(0.17, 1.26, 0.13)], 0.008, fire, 0.03);
  glowSprite(chest, '#ff9a3a', 0.4, V(0.02, 1.4, 0.24), 0.5);
  // The collar: a stiff open cone standing behind the head.
  const collar = M(new THREE.CylinderGeometry(0.2, 0.12, 0.22, 12, 1, true, Math.PI * 0.35, Math.PI * 1.3), glassD, chest);
  collar.position.set(0, 1.7, -0.01);
  for (const k of [-1, 1]) {
    const sh = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), robe, chest);
    sh.position.set(k * 0.27, 1.56, 0);
    sh.scale.set(0.13, 0.08, 0.14);
  }

  /* ---------- the mantle of smoke and ash ---------- */
  const mantle = G(chest, 0, 1.62, -0.18);
  for (const [x, w, h, rz] of [[-0.16, 0.28, 1.3, 0.06], [0.16, 0.28, 1.25, -0.06], [0, 0.32, 1.42, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.12, 0, rz);
  }

  /* ---------- head: a glass face, ember eyes, a skullcap, a beard of ash ---------- */
  const headB = bone(chest, V(0, 1.66, 0.01), 'head');
  const head = headB.abs;
  M(new THREE.SphereGeometry(0.115, 9, 7), glass, head).position.set(0, 1.8, 0.02);
  const cap = M(new THREE.SphereGeometry(0.122, 10, 5, 0, Math.PI * 2, 0, Math.PI * 0.42), char, head);
  cap.position.set(0, 1.81, 0.01);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.022, 6, 4), fire, head).position.set(k * 0.042, 1.81, 0.12);
    glowSprite(head, '#ff9a3a', 0.22, V(k * 0.042, 1.81, 0.15), 0.95);
  }
  k4.crack(head, [V(0.06, 1.88, 0.09), V(0.05, 1.82, 0.11), V(0.07, 1.75, 0.1)], 0.006, fire, 0.02);
  // The beard: grey ash falling in a long point, embers glinting in it.
  const beard = M(new THREE.ConeGeometry(0.075, 0.3, 7), std('#3b3532', { roughness: 1 }), head);
  beard.position.set(0, 1.62, 0.1);
  beard.rotation.x = Math.PI + 0.25;
  M(new THREE.SphereGeometry(0.012, 4, 3), fire, head).position.set(0.02, 1.6, 0.14);

  /* ---------- the book hand: an open book, pages of fire that never burn away ---------- */
  const armLB = bone(chest, V(-0.29, 1.54, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.3, 1.54, 0), V(-0.34, 1.22, 0.05), 0.075, 0.065, robe, 6);
  const foreLB = bone(aL, V(-0.34, 1.21, 0.06), 'foreL');
  const fL = foreLB.abs;
  limb(fL, V(-0.34, 1.21, 0.06), V(-0.26, 1.12, 0.3), 0.065, 0.05, glassD, 6);
  const shieldB = bone(fL, V(-0.25, 1.11, 0.33), 'shield');
  const sh = shieldB.abs;
  {
    const bk = G(sh, -0.22, 1.13, 0.38);
    bk.rotation.set(-0.55, 0.2, 0);
    for (const k of [-1, 1]) {
      const cov = M(new THREE.BoxGeometry(0.2, 0.012, 0.27), char, bk);
      cov.position.set(k * 0.1, 0, 0);
      cov.rotation.z = -k * 0.18;
      const pg = M(new THREE.BoxGeometry(0.18, 0.02, 0.25), page, bk);
      pg.position.set(k * 0.095, 0.016, 0);
      pg.rotation.z = -k * 0.18;
    }
    // Pages lifting off it, burning at their edges.
    const flames: THREE.Mesh[] = [];
    for (const [x, z, h] of [[0.05, 0.02, 0.18], [-0.05, -0.03, 0.14], [0.0, 0.05, 0.11]] as const) {
      const f = M(new THREE.ConeGeometry(0.035, h, 6), fireF, bk);
      f.position.set(x, 0.03 + h / 2, z);
      flames.push(f);
    }
    M(new THREE.BoxGeometry(0.2, 0.01, 0.01), fire, bk).position.set(0, 0.03, 0.125);
    glowSprite(bk, '#ff9a3a', 0.5, V(0, 0.1, 0), 0.7);
    bookFlames.push(...flames);
  }

  /* ---------- the staff hand: a charred staff crowned with a cage of burning pages ---------- */
  const armRB = bone(chest, V(0.29, 1.54, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.3, 1.54, 0), V(0.35, 1.21, 0.02), 0.075, 0.065, robe, 6);
  const foreRB = bone(aR, V(0.35, 1.2, 0.02), 'foreR');
  const fR = foreRB.abs;
  limb(fR, V(0.35, 1.2, 0.02), V(0.38, 0.98, 0.14), 0.065, 0.05, glassD, 6);
  const swordB = bone(fR, V(0.38, 0.96, 0.16), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.075, 0.085, 0.085), glassD, sw).position.set(0.38, 0.96, 0.16);
  {
    const st = G(sw, 0.38, 0.96, 0.18);
    st.rotation.z = -0.05;
    M(new THREE.CylinderGeometry(0.022, 0.028, 1.9, 6), char, st).position.y = 0.38;
    k4.crack(st, [V(0.02, -0.3, 0.02), V(0.025, 0.1, 0.02), V(0.02, 0.6, 0.02), V(0.024, 1.1, 0.02)], 0.006, fire, 0.01);
    // The crown: a cage of iron-black ribs, a fire of pages inside.
    const cr = G(st, 0, 1.38, 0);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const rib = M(new THREE.BoxGeometry(0.014, 0.22, 0.014), glassD, cr);
      rib.position.set(Math.sin(a) * 0.07, 0.08, Math.cos(a) * 0.07);
      rib.rotation.set(Math.cos(a) * 0.35, 0, -Math.sin(a) * 0.35);
    }
    for (const [x, z, h] of [[0, 0, 0.2], [0.03, -0.02, 0.14], [-0.03, 0.02, 0.12]] as const) {
      const f = M(new THREE.ConeGeometry(0.04, h, 6), fireF, cr);
      f.position.set(x, 0.04 + h / 2, z);
      staffFlames.push(f);
    }
    M(new THREE.SphereGeometry(0.035, 6, 5), fire, cr).position.y = 0.05;
    glowSprite(cr, '#ff9a3a', 0.6, V(0, 0.12, 0), 0.75);
  }

  /* ---------- smoke and ash breathing off him ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.26, -0.18, 0.65, 1.5], [-0.24, -0.2, 0.6, 1.45], [0.04, -0.28, 0.85, 1.25], [-0.22, 0.3, 0.5, 1.15]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.045, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    [...bookFlames, ...staffFlames].forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, ANSEL_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
